import React, { useRef } from 'react'
import { Upload, CheckCircle, X, AlertCircle, Plus } from 'lucide-react'
import { Button } from '../ui/Button'
import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'
import type { HubColor } from './HubShell'

const fileUploadZoneBorderClasses: Record<HubColor, string> = {
  green: 'border-green-300 dark:border-green-700',
  blue: 'border-blue-300 dark:border-blue-700',
  purple: 'border-purple-300 dark:border-purple-700',
  orange: 'border-orange-300 dark:border-orange-700',
  cyan: 'border-cyan-300 dark:border-cyan-700',
}

const fileUploadZoneIconClasses: Record<HubColor, string> = {
  green: 'text-green-500 dark:text-green-400',
  blue: 'text-blue-500 dark:text-blue-400',
  purple: 'text-purple-500 dark:text-purple-400',
  orange: 'text-orange-500 dark:text-orange-400',
  cyan: 'text-cyan-500 dark:text-cyan-400',
}

const fileUploadZoneBgClasses: Record<HubColor, string> = {
  green: 'bg-green-50 dark:bg-green-900/10',
  blue: 'bg-blue-50 dark:bg-blue-900/10',
  purple: 'bg-purple-50 dark:bg-purple-900/10',
  orange: 'bg-orange-50 dark:bg-orange-900/10',
  cyan: 'bg-cyan-50 dark:bg-cyan-900/10',
}

const fileUploadZoneSuccessBgClasses: Record<HubColor, string> = {
  green: 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700',
  blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700',
  purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700',
  orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700',
  cyan: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-300 dark:border-cyan-700',
}

const fileUploadZoneSuccessTextClasses: Record<HubColor, string> = {
  green: 'text-green-700 dark:text-green-300',
  blue: 'text-blue-700 dark:text-blue-300',
  purple: 'text-purple-700 dark:text-purple-300',
  orange: 'text-orange-700 dark:text-orange-300',
  cyan: 'text-cyan-700 dark:text-cyan-300',
}

export interface FileUploadZoneProps {
  accept?: string
  label: string
  hint?: string
  state: 'idle' | 'verifying' | 'success' | 'error'
  filename?: string
  color: HubColor
  onSelect: (file: File) => void
  onRemove?: () => void
  summary?: React.ReactNode
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  accept,
  label,
  hint,
  state,
  filename,
  color,
  onSelect,
  onRemove,
  summary,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (file) onSelect(file)
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const file = event.dataTransfer.files?.[0]
    if (file) onSelect(file)
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
  }

  if (state === 'success') {
    return (
      <div
        className={cn(
          'rounded-lg border p-4 space-y-2',
          fileUploadZoneSuccessBgClasses[color]
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span
              className={cn(
                'text-sm font-medium',
                fileUploadZoneSuccessTextClasses[color]
              )}
            >
              {filename ?? 'File uploaded'}
            </span>
          </div>
          {onRemove && (
            <Button
              variant="ghost"
              size="icon"
              title="Remove file"
              onClick={onRemove}
              className="h-7 w-7"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {summary && <div className="pt-1">{summary}</div>}
      </div>
    )
  }

  if (state === 'verifying') {
    return (
      <div
        className={cn(
          'rounded-lg border-2 border-dashed p-6 flex flex-col items-center gap-2',
          fileUploadZoneBorderClasses[color],
          fileUploadZoneBgClasses[color]
        )}
      >
        <div className="h-5 w-5 rounded-full border-2 border-current border-t-transparent animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Verifying… extracting balance</p>
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="rounded-lg border-2 border-dashed border-destructive bg-destructive/5 p-6 flex flex-col items-center gap-2">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-destructive font-medium">Upload failed</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
        >
          Retry
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="sr-only"
          aria-label={label}
          onChange={handleChange}
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'rounded-lg border-2 border-dashed p-6 flex flex-col items-center gap-2 cursor-pointer transition-colors',
        fileUploadZoneBorderClasses[color],
        fileUploadZoneBgClasses[color]
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
      }}
    >
      <Upload className={cn('h-6 w-6', fileUploadZoneIconClasses[color])} />
      <p className="text-sm font-medium text-foreground">{label}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        aria-label={label}
        onChange={handleChange}
      />
    </div>
  )
}

const twoMethodToggleSelectedClasses: Record<HubColor, string> = {
  green: 'bg-green-600 text-white dark:bg-green-600',
  blue: 'bg-blue-600 text-white dark:bg-blue-600',
  purple: 'bg-purple-600 text-white dark:bg-purple-600',
  orange: 'bg-orange-600 text-white dark:bg-orange-600',
  cyan: 'bg-cyan-600 text-white dark:bg-cyan-600',
}

export interface TwoMethodToggleOption {
  id: string
  label: string
  icon?: LucideIcon
}

export interface TwoMethodToggleProps {
  value: string
  options: TwoMethodToggleOption[]
  onChange: (id: string) => void
  color: HubColor
}

export const TwoMethodToggle: React.FC<TwoMethodToggleProps> = ({
  value,
  options,
  onChange,
  color,
}) => {
  return (
    <div
      className="inline-flex rounded-full bg-muted p-1 gap-1"
      role="radiogroup"
    >
      {options.map((option) => {
        const selected = option.id === value
        const Icon = option.icon
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all',
              selected
                ? twoMethodToggleSelectedClasses[color]
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {Icon && <Icon className="h-4 w-4" />}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

const optionCardSelectedBorderClasses: Record<HubColor, string> = {
  green: 'border-green-500 dark:border-green-500',
  blue: 'border-blue-500 dark:border-blue-500',
  purple: 'border-purple-500 dark:border-purple-500',
  orange: 'border-orange-500 dark:border-orange-500',
  cyan: 'border-cyan-500 dark:border-cyan-500',
}

const optionCardSelectedBgClasses: Record<HubColor, string> = {
  green: 'bg-green-50 dark:bg-green-900/20',
  blue: 'bg-blue-50 dark:bg-blue-900/20',
  purple: 'bg-purple-50 dark:bg-purple-900/20',
  orange: 'bg-orange-50 dark:bg-orange-900/20',
  cyan: 'bg-cyan-50 dark:bg-cyan-900/20',
}

const optionCardSelectedTextClasses: Record<HubColor, string> = {
  green: 'text-green-700 dark:text-green-300',
  blue: 'text-blue-700 dark:text-blue-300',
  purple: 'text-purple-700 dark:text-purple-300',
  orange: 'text-orange-700 dark:text-orange-300',
  cyan: 'text-cyan-700 dark:text-cyan-300',
}

export interface OptionCardGridOption {
  id: string
  label: string
  description?: string
}

export interface OptionCardGridProps {
  value: string
  options: OptionCardGridOption[]
  onChange: (id: string) => void
  color: HubColor
}

export const OptionCardGrid: React.FC<OptionCardGridProps> = ({
  value,
  options,
  onChange,
  color,
}) => {
  return (
    <div className="flex flex-col gap-2" role="radiogroup">
      {options.map((option) => {
        const selected = option.id === value
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.label}
            onClick={() => onChange(option.id)}
            className={cn(
              'w-full text-left px-4 py-3 rounded-lg border-2 transition-all',
              selected
                ? cn(
                    optionCardSelectedBorderClasses[color],
                    optionCardSelectedBgClasses[color]
                  )
                : 'border-border bg-background hover:bg-muted/50'
            )}
          >
            <p
              className={cn(
                'text-sm font-medium',
                selected ? optionCardSelectedTextClasses[color] : 'text-foreground'
              )}
            >
              {option.label}
            </p>
            {option.description && (
              <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
            )}
          </button>
        )
      })}
    </div>
  )
}

const conditionalInfoBoxBorderClasses: Record<HubColor, string> = {
  green: 'border-green-200 dark:border-green-800',
  blue: 'border-blue-200 dark:border-blue-800',
  purple: 'border-purple-200 dark:border-purple-800',
  orange: 'border-orange-200 dark:border-orange-800',
  cyan: 'border-cyan-200 dark:border-cyan-800',
}

const conditionalInfoBoxBgClasses: Record<HubColor, string> = {
  green: 'bg-green-50 dark:bg-green-900/10',
  blue: 'bg-blue-50 dark:bg-blue-900/10',
  purple: 'bg-purple-50 dark:bg-purple-900/10',
  orange: 'bg-orange-50 dark:bg-orange-900/10',
  cyan: 'bg-cyan-50 dark:bg-cyan-900/10',
}

const conditionalInfoBoxIconClasses: Record<HubColor, string> = {
  green: 'text-green-600 dark:text-green-400',
  blue: 'text-blue-600 dark:text-blue-400',
  purple: 'text-purple-600 dark:text-purple-400',
  orange: 'text-orange-600 dark:text-orange-400',
  cyan: 'text-cyan-600 dark:text-cyan-400',
}

const conditionalInfoBoxTitleClasses: Record<HubColor, string> = {
  green: 'text-green-800 dark:text-green-200',
  blue: 'text-blue-800 dark:text-blue-200',
  purple: 'text-purple-800 dark:text-purple-200',
  orange: 'text-orange-800 dark:text-orange-200',
  cyan: 'text-cyan-800 dark:text-cyan-200',
}

export interface ConditionalInfoBoxProps {
  icon?: LucideIcon
  title: string
  children: React.ReactNode
  color: HubColor
}

export const ConditionalInfoBox: React.FC<ConditionalInfoBoxProps> = ({
  icon: Icon,
  title,
  children,
  color,
}) => {
  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        conditionalInfoBoxBorderClasses[color],
        conditionalInfoBoxBgClasses[color]
      )}
    >
      <div className="flex items-start gap-3">
        {Icon && (
          <Icon
            className={cn('h-5 w-5 flex-shrink-0 mt-0.5', conditionalInfoBoxIconClasses[color])}
          />
        )}
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-sm font-semibold mb-1',
              conditionalInfoBoxTitleClasses[color]
            )}
          >
            {title}
          </p>
          <div className="text-sm text-muted-foreground">{children}</div>
        </div>
      </div>
    </div>
  )
}

const multiRowAddButtonClasses: Record<HubColor, string> = {
  green: 'text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20',
  blue: 'text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20',
  purple: 'text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20',
  orange: 'text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20',
  cyan: 'text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700 hover:bg-cyan-50 dark:hover:bg-cyan-900/20',
}

const multiRowHeaderColorClasses: Record<HubColor, string> = {
  green: 'text-green-800 dark:text-green-200',
  blue: 'text-blue-800 dark:text-blue-200',
  purple: 'text-purple-800 dark:text-purple-200',
  orange: 'text-orange-800 dark:text-orange-200',
  cyan: 'text-cyan-800 dark:text-cyan-200',
}

export interface MultiRowFormSectionRow {
  id: string
  content: React.ReactNode
}

export interface MultiRowFormSectionProps {
  title: string
  rows: MultiRowFormSectionRow[]
  onAdd?: () => void
  onRemove?: (id: string) => void
  addLabel?: string
  color: HubColor
}

export const MultiRowFormSection: React.FC<MultiRowFormSectionProps> = ({
  title,
  rows,
  onAdd,
  onRemove,
  addLabel = 'Add',
  color,
}) => {
  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className={cn('text-sm font-semibold', multiRowHeaderColorClasses[color])}>
          {title}
        </h4>
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className={cn(
              'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border transition-colors',
              multiRowAddButtonClasses[color]
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            {addLabel}
          </button>
        )}
      </div>
      <div className="space-y-3">
        {rows.map((row) => (
          <div
            key={row.id}
            className="relative rounded-md border border-border bg-background/60 p-4"
          >
            {onRemove && (
              <Button
                variant="ghost"
                size="icon"
                title="Remove row"
                onClick={() => onRemove(row.id)}
                className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
            <div className={onRemove ? 'pr-8' : ''}>{row.content}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
